/**
 * GlossaryHint — Astuce éducative one-shot.
 * S'affiche en bas de la page la 1ère fois qu'un utilisateur arrive sur le Cockpit
 * pour leur expliquer que les mots soulignés en pointillés ouvrent une définition
 * au survol. Dismissable, jamais ré-affiché (localStorage).
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X } from 'lucide-react';

const STORAGE_KEY = 'homly_glossary_hint_seen_v1';

export default function GlossaryHint({ delay = 2500 }) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (localStorage.getItem(STORAGE_KEY) === '1') return;
        const t = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(t);
    }, [delay]);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* silent */ }
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 30, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="fixed bottom-4 right-4 z-50 max-w-sm bg-card/95 backdrop-blur-xl border border-[#FF914D]/30 rounded-2xl shadow-2xl shadow-[#FF914D]/10 p-4 pr-10"
                    data-testid="glossary-hint"
                >
                    <button
                        onClick={dismiss}
                        className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/[0.04] text-muted-foreground hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"
                        data-testid="glossary-hint-dismiss"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>

                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF914D] to-[#681793] flex items-center justify-center flex-shrink-0 shadow-md">
                            <BookOpen className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold text-foreground mb-1">
                                💡 Astuce — Mots expliqués
                            </p>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                Les mots <span className="underline decoration-dotted decoration-[#FF914D] decoration-1 underline-offset-2 text-foreground/90 font-medium">soulignés en pointillés</span> dans l'app
                                {' '}sont expliqués automatiquement. <strong className="text-foreground">Survolez-les</strong> pour
                                {' '}voir la définition.
                            </p>
                            <button
                                onClick={dismiss}
                                className="mt-3 text-[11px] font-bold text-[#FF914D] hover:text-[#FF914D]/80 transition-colors"
                                data-testid="glossary-hint-got-it"
                            >
                                J'ai compris ✓
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
